// core/script.js — script text: sections, cues, words, timing and voice matching.
import { esc, clamp } from './util'

// Things in the script that aren't read aloud:
//   [cue]            a short stage direction, shown small (up to 32 letters)
//   [[note]]         a longer note to yourself (up to 240 letters)
//   ?? Button | Title | extra words   an answer card for Call Mode, until the next # heading or ?? line
export const CUE_RE = /\[\[[^\]]{1,240}\]\]|\[[^\]]{1,32}\]/g
const CUE_SPLIT = /(\[\[[^\]]{1,240}\]\]|\[[^\]]{1,32}\])/
const CUE_WHOLE = new RegExp(`^(?:${CUE_RE.source})$`)
const ANSWER_RE = /^\?\?\s*(.+)$/
export const WPM = { min: 60, max: 260, step: 10 }
export const FS = { min: 24, max: 120, step: 4 }

/** "# Heading" lines become sections; blank lines split paragraphs. Answer cards (??) are left out of the scroll. */
export function parseScript(text) {
  const blocks = []
  let para = []
  let inAnswer = false
  const flush = () => { if (para.length) { blocks.push({ t: 'p', s: para.join(' ') }); para = [] } }
  String(text ?? '').replace(/\r/g, '').split('\n').forEach((line) => {
    const l = line.trim()
    if (ANSWER_RE.test(l)) { flush(); inAnswer = true; return }
    if (l.startsWith('#')) { inAnswer = false; flush(); const h = l.replace(/^#+\s*/, ''); if (h) blocks.push({ t: 'h', s: h }); return }
    if (inAnswer) return
    if (!l) { flush(); return }
    para.push(l)
  })
  flush()
  return blocks
}

/**
 * Call Mode answers: "?? Price | They say it's too expensive | cost, budget"
 * followed by the answer's lines. → [{ label, title, keys, text }]
 */
export function parseAnswers(text) {
  const out = []
  let cur = null
  String(text ?? '').replace(/\r/g, '').split('\n').forEach((line) => {
    const l = line.trim()
    const m = l.match(ANSWER_RE)
    if (m) {
      const [label, title, keys] = m[1].split('|').map((x) => x.trim())
      cur = { label: (label || 'Answer').slice(0, 24), title: (title || label || '').slice(0, 80), keys: keys || '', lines: [] }
      out.push(cur); return
    }
    if (l.startsWith('#')) { cur = null; return }
    if (cur) cur.lines.push(line)
  })
  return out.map((a) => ({ label: a.label, title: a.title, keys: a.keys, text: a.lines.join('\n').trim() })).slice(0, 40)
}

/** **bold** in a line: → [{ text, b }] pieces. */
function boldPieces(s) {
  return String(s).split(/(\*\*[^*]+\*\*)/).filter(Boolean).map((x) => (/^\*\*[^*]+\*\*$/.test(x) ? { text: x.slice(2, -2), b: true } : { text: x, b: false }))
}

export const spokenWords = (s) => s.replace(CUE_RE, ' ').replace(/\*\*/g, '').split(/\s+/).filter(Boolean)
const cueHtml = (part) => (part.startsWith('[[') ? `<span class="cue note">${esc(part.slice(2, -2))}</span>` : `<span class="cue">${esc(part.slice(1, -1))}</span>`)
export const inlineHtml = (s) => s.split(CUE_SPLIT).map((part) => (CUE_WHOLE.test(part) ? cueHtml(part) : esc(part.replace(/\*\*/g, '')))).join('')

/** Every spoken word gets a span so the prompter knows where it sits. **bold** words get emphasis. */
export function inlineWords(s) {
  return s.split(CUE_SPLIT).map((part) => {
    if (CUE_WHOLE.test(part)) return cueHtml(part)
    return boldPieces(part).map(({ text, b }) => text.split(/(\s+)/).map((tok) => (tok.trim() ? `<span class="w${b ? ' em' : ''}">${esc(tok)}</span>` : tok)).join('')).join('')
  }).join('')
}

export function scriptHtml(blocks, emptyText = 'Your script is empty. Tap the pencil to write one or import a file.') {
  return blocks.length
    ? blocks.map((b) => (b.t === 'h' ? `<h2 class="sec">${inlineHtml(b.s)}</h2>` : `<p>${inlineWords(b.s)}</p>`)).join('')
    : `<p class="empty">${esc(emptyText)}</p>`
}

export const countScriptWords = (text) => parseScript(text).reduce((n, b) => n + (b.t === 'p' ? spokenWords(b.s).length : 0), 0)

export function normWord(w) {
  return String(w).toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\p{L}\p{N}]/gu, '')
}

export function titleOf(text) {
  const b = parseScript(text)
  const h = b.find((x) => x.t === 'h'), p = b.find((x) => x.t === 'p')
  return ((h ? h.s : p ? spokenWords(p.s).slice(0, 6).join(' ') : '') || 'Untitled script').slice(0, 60)
}

/** "2:30" → 150 s, "2.5" or "2" → minutes. */
export function parseDuration(str) {
  const t = String(str || '').trim().replace(',', '.')
  if (!t) return 0
  if (t.includes(':')) { const [m, sec] = t.split(':'); return (+m || 0) * 60 + (+sec || 0) }
  const n = parseFloat(t)
  return isFinite(n) ? n * 60 : 0
}

export function fitWpm(text, durationText) {
  const sec = parseDuration(durationText), words = countScriptWords(text)
  if (!sec || !words) return null
  const raw = words / (sec / 60)
  return { raw, wpm: clamp(Math.round(raw), WPM.min, WPM.max) }
}

/**
 * Voice follow: find where the newest heard words sit in the script, only
 * moving forward. Bigger jumps need more matching words so a stray phrase
 * can't fling the script ahead.
 */
export function matchForward(W, heard, from) {
  const n = W.length
  if (!n || !heard.length) return from
  const lo = Math.max(0, from - 4), hi = Math.min(n, from + 45)
  let best = -1, bestScore = 0
  for (let drop = 0; drop <= 1; drop++) {   // the newest interim word is often wrong, so also try without it
    const tail = heard.slice(Math.max(0, heard.length - 6 - drop), heard.length - drop)
    if (!tail.length) continue
    for (let j = lo; j < hi; j++) {
      let score = 0, k = tail.length - 1, jj = j
      while (k >= 0 && jj >= lo) {
        const a = W[jj], b = tail[k]
        if (a === b || (b.length > 3 && a.length > 3 && a.slice(0, 4) === b.slice(0, 4))) { score += a.length > 3 ? 2 : 1; k--; jj-- }
        else break
      }
      if (score > bestScore || (score === bestScore && score > 0 && j < best)) { bestScore = score; best = j }
    }
  }
  if (best < 0) return from
  const jumpBy = best + 1 - from
  const need = jumpBy > 15 ? 5 : jumpBy > 5 ? 3 : 2
  return bestScore >= need && best + 1 > from ? best + 1 : from
}

/* ---- importing files ---- */
export function rtfToText(rtf) {
  return rtf.replace(/\\par[d]?/g, '\n').replace(/\{\\\*[^{}]*\}/g, '').replace(/\\'[0-9a-f]{2}/gi, '')
    .replace(/\\[a-z]+-?\d* ?/gi, '').replace(/[{}]/g, '').replace(/\n{3,}/g, '\n\n').trim()
}
export function markdownToScript(md) {
  return md.replace(/^\s*[-*+]\s+/gm, '').replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1').replace(/(^|\s)\*([^*]+)\*/g, '$1$2')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '').replace(/\[([^\]]+)\]\((?:https?:|mailto:)[^)]*\)/g, '$1')
    .replace(/^>\s?/gm, '').replace(/`([^`]+)`/g, '$1')
}
/** Word files: mammoth loads only when someone imports one (and is cached for offline use after). */
export async function docxToScript(buf) {
  const mammoth = (await import('mammoth/mammoth.browser.js')).default
  const { value } = await mammoth.convertToHtml({ arrayBuffer: buf })
  const doc = new DOMParser().parseFromString(value, 'text/html')
  return [...doc.body.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li')]
    .map((el) => { const t = el.textContent.trim(); return !t ? '' : /^H\d$/.test(el.tagName) ? '# ' + t : t })
    .filter(Boolean).join('\n\n')
}
/** → { text, name } or throws Error with a friendly message. */
export async function importFile(file, maxLen) {
  const name = file.name.replace(/\.[^.]+$/, '')
  const ext = (file.name.split('.').pop() || '').toLowerCase()
  let text
  if (ext === 'docx') text = await docxToScript(await file.arrayBuffer())
  else if (['doc', 'pdf', 'pages'].includes(ext)) throw new Error("That file type can't be read here. Save it as .docx or .txt, or copy and paste the text.")
  else {
    const raw = await file.text()
    text = ext === 'rtf' ? rtfToText(raw) : ext === 'md' || ext === 'markdown' ? markdownToScript(raw) : raw
  }
  text = text.replace(/\r\n?/g, '\n').trim()
  if (!text) throw new Error('That file has no text we could read.')
  const cut = text.length > maxLen
  return { text: cut ? text.slice(0, maxLen) : text, name, cut }
}
